import {
  ShieldCheck,
  HeartHandshake,
  Plane,
  Users,
  FileUp,
  Hospital,
  PlaneLanding,
  LifeBuoy,
  HeartPulse,
  Bone,
  Ribbon,
  Brain,
  MoveVertical,
  Droplets,
  Activity,
  Baby,
  BedDouble,
  Languages,
  CalendarCheck,
  Car,
} from "lucide-react";

export const BRAND = {
  name: "GetWellSoon",
  domain: "GetWellSoon.com",
  email: "getwellsoon4me@gmail.com",
  whatsappDisplay: "+91 8796053290",
  whatsappLink: "https://wa.me/918796053290",
};

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/treatments", label: "Treatments" },
  { to: "/why-india", label: "Why India" },
  { to: "/patient-support", label: "Patient Support" },
  { to: "/about", label: "About Us" },
];

export const DISCLAIMER =
  "GetWellSoon provides coordination and support services only. We do not provide medical advice, diagnosis, or treatment. All medical decisions, treatment plans, and clinical care are the sole responsibility of the treating hospital and healthcare professionals.";

export const TRUST_PILLARS = [
  {
    icon: ShieldCheck,
    title: "Transparent coordination",
    text: "Clear hospital options, itemized estimates from the hospitals themselves, and no hidden coordination fees.",
  },
  {
    icon: HeartHandshake,
    title: "Dedicated patient support",
    text: "One care coordinator who knows your case — from your first call to your safe return home.",
  },
  {
    icon: Plane,
    title: "Travel & accommodation assistance",
    text: "Visa documentation guidance, flight planning, and accommodation close to your hospital.",
  },
  {
    icon: Users,
    title: "Family assistance during treatment",
    text: "Practical, day-to-day support for the family members traveling alongside the patient.",
  },
];

export const STEPS = [
  {
    icon: FileUp,
    title: "Share your medical reports",
    short: "Send your records securely and tell us what you are looking for.",
    description:
      "Everything begins with your medical history. Share reports, scans, and diagnoses securely, and a care coordinator will review your case with you to understand exactly what you need.",
    points: [
      "Secure sharing by upload, email, or WhatsApp — your records stay private",
      "A coordinator reviews your case history with you",
      "Your questions are documented for hospital review",
    ],
  },
  {
    icon: Hospital,
    title: "Receive hospital options",
    short: "Suitable hospitals and specialists, with indicative estimates.",
    description:
      "We present a shortlist of suitable hospitals and specialists in India, with indicative estimates based on your actual reports — so you can compare and decide with confidence.",
    points: [
      "A focused shortlist of hospitals and specialists — not a directory",
      "Indicative estimates based on your actual reports",
      "Time to compare, ask questions, and decide — no pressure",
    ],
  },
  {
    icon: Plane,
    title: "Plan your travel",
    short: "Appointments, visa letters, and accommodation — confirmed in advance.",
    description:
      "Once you decide, we confirm your hospital appointments, coordinate medical visa invitation letters, and arrange accommodation near your hospital before you book anything.",
    points: [
      "Appointments confirmed before you book flights",
      "Medical visa invitation letters issued by the hospital",
      "Accommodation near your hospital, matched to your stay",
    ],
  },
  {
    icon: PlaneLanding,
    title: "Arrive in India",
    short: "Airport pickup, check-in, and hospital introductions, arranged ahead.",
    description:
      "A confirmed driver meets you at the airport. Your accommodation, orientation, and introduction to the hospital's international patient team are all arranged before you land.",
    points: [
      "Airport pickup with a confirmed driver",
      "Check-in, orientation, and a local SIM if needed",
      "In-person introduction to your hospital's international patient team",
    ],
  },
  {
    icon: LifeBuoy,
    title: "Ongoing support during your stay",
    short: "Your coordinator stays with you through treatment and recovery.",
    description:
      "Throughout treatment and recovery, your coordinator remains reachable — helping with logistics, communication, and planning a well-documented return home.",
    points: [
      "Your coordinator reachable throughout treatment",
      "Help with billing questions, pharmacy, and daily logistics",
      "Discharge planning, complete records, and follow-up for your return",
    ],
  },
];

export const TREATMENTS = [
  {
    slug: "cardiology",
    icon: HeartPulse,
    name: "Cardiology",
    description:
      "Heart evaluations, angioplasty, bypass surgery, and valve procedures — coordinated with internationally accredited cardiac centers.",
  },
  {
    slug: "orthopedics",
    icon: Bone,
    name: "Orthopedics",
    description:
      "Knee and hip replacement, sports injury care, and fracture management at dedicated orthopedic institutes.",
  },
  {
    slug: "oncology",
    icon: Ribbon,
    name: "Oncology",
    description:
      "Second opinions, diagnostics, chemotherapy, radiation therapy, and surgical oncology — coordinated with comprehensive cancer centers.",
  },
  {
    slug: "neurology",
    icon: Brain,
    name: "Neurology",
    description:
      "Evaluation and surgical care for brain, nerve, and movement disorders with experienced neurology and neurosurgery teams.",
  },
  {
    slug: "spine-care",
    icon: MoveVertical,
    name: "Spine Care",
    description:
      "Disc surgery, spinal fusion, and minimally invasive spine procedures at specialized spine units.",
  },
  {
    slug: "kidney-care",
    icon: Droplets,
    name: "Kidney Care",
    description:
      "Nephrology consultations, dialysis planning, and transplant program coordination through authorized hospital programs.",
  },
  {
    slug: "liver-care",
    icon: Activity,
    name: "Liver Care",
    description:
      "Hepatology evaluations and advanced liver care, including transplant program coordination where clinically appropriate.",
  },
  {
    slug: "ivf-fertility",
    icon: Baby,
    name: "IVF & Fertility",
    description:
      "Fertility assessments, IVF cycles, and reproductive medicine programs at established fertility centers.",
  },
];

export const WHY_INDIA = [
  {
    title: "Experienced specialists",
    short: "Senior doctors with deep, high-volume clinical experience.",
    detail:
      "Many Indian specialists have trained or practiced internationally and manage high patient volumes across complex specialties. We help you review the credentials and experience of the specific doctors being considered for your care, so your decision is based on facts, not brochures.",
  },
  {
    title: "Advanced hospital infrastructure",
    short: "Modern surgical, intensive care, and diagnostic facilities.",
    detail:
      "India's leading private hospitals operate modern surgical suites, intensive care units, and diagnostic facilities, and many hold recognized international accreditations. We share accreditation and facility details openly so you can verify them independently.",
  },
  {
    title: "International patient departments",
    short: "Dedicated teams that work with overseas patients every day.",
    detail:
      "Major hospitals maintain dedicated international patient departments that handle medical visa letters, appointment scheduling, and the practical needs of visiting families. We work directly with these teams on your behalf, which keeps communication organized.",
  },
  {
    title: "English-speaking medical teams",
    short: "Clear consultations, consent discussions, and records.",
    detail:
      "English is the primary language of medical education and clinical communication in India. Consultations, consent discussions, and medical records are conducted and documented in English, which helps international patients stay fully informed at every step.",
  },
  {
    title: "Coordinated care",
    short: "Diagnostics, treatment, and rehabilitation under one roof.",
    detail:
      "At leading hospitals, diagnostics, treatment, and rehabilitation are typically managed within a single campus, which can simplify logistics considerably for international patients. Our role is to keep every step scheduled, sequenced, and clearly communicated.",
  },
];

export const SUPPORT_SERVICES = [
  {
    icon: PlaneLanding,
    title: "Airport pickup coordination",
    text: "A confirmed driver meets you on arrival — no negotiating transport after a long international flight.",
  },
  {
    icon: BedDouble,
    title: "Hotel & accommodation assistance",
    text: "Options near your hospital, matched to your budget and length of stay, and booked in advance.",
  },
  {
    icon: Languages,
    title: "Interpreter support",
    text: "Language assistance for hospital visits and daily needs, arranged where required.",
  },
  {
    icon: CalendarCheck,
    title: "Appointment scheduling",
    text: "Consultations, diagnostics, and follow-ups scheduled and confirmed before you travel.",
  },
  {
    icon: Car,
    title: "Local transportation guidance",
    text: "Reliable transport between your hotel, the hospital, and the city — arranged or advised by our team.",
  },
  {
    icon: Users,
    title: "Family support services",
    text: "Practical help for the people traveling with you — from meals and errands to local SIM cards.",
  },
];

export const FAQS = [
  {
    q: "Is GetWellSoon a hospital or medical provider?",
    a: "No. GetWellSoon is a patient coordination company. We organize appointments, travel, accommodation, and on-ground support in India. All medical advice, diagnosis, and treatment come solely from the treating hospital and its physicians.",
  },
  {
    q: "How do I get a treatment estimate?",
    a: "Share your medical reports through our contact form or WhatsApp. We coordinate with suitable hospitals in India and return indicative estimates based on your actual records — never generic price lists. Final treatment plans and costs are confirmed by the treating hospital.",
  },
  {
    q: "Can a family member travel with me?",
    a: "Yes. Most patients travel with a companion, and hospitals in India are accustomed to accommodating attendants. We arrange accommodation, local transport, and day-to-day support for your family as well.",
  },
  {
    q: "What visa do I need for treatment in India?",
    a: "Most patients travel on a Medical (MED) visa, and accompanying family members on a Medical Attendant visa. Hospitals issue the required invitation letters, and we guide you through the documentation. Visa decisions rest solely with the Government of India.",
  },
  {
    q: "Who decides my treatment plan?",
    a: "Only your treating physicians. We never recommend one procedure or hospital over another on medical grounds. Our job is to make sure your questions reach the right doctors — and their answers reach you clearly.",
  },
  {
    q: "What happens when I return home?",
    a: "Before you leave India, we help ensure you have complete medical records, discharge summaries, and a follow-up plan you can share with your physician at home.",
  },
];
